import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { Loader2, ShieldCheck, CheckCircle2, CalendarDays, Users, MapPin, MessageCircle, Mail, Lock } from "lucide-react";
import { fetchTourPay, createTourPayOrder, verifyTourPay, inr, formatApiError } from "@/lib/api";

const INST_CHIP = { pending: "bg-stone-200 text-stone-600", paid: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-600", cancelled: "bg-red-100 text-red-600" };

export default function TourPayPage() {
    const { token } = useParams();
    const { Razorpay } = useRazorpay();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [justPaid, setJustPaid] = useState(null);

    const load = () => fetchTourPay(token).then(setData).catch((e) => setError(formatApiError(e.response?.data?.detail)));
    useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const payNow = async () => {
        setBusy(true);
        try {
            const order = await createTourPayOrder(token);
            const rzp = new Razorpay({
                key: order.key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "GhumneChaley.com",
                description: `${data.tour.name} — instalment ${order.installment} (${order.ref})`,
                order_id: order.order_id,
                prefill: order.prefill,
                theme: { color: "#F26522" },
                handler: async (resp) => {
                    try {
                        const result = await verifyTourPay(token, {
                            order_id: resp.razorpay_order_id,
                            payment_id: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                        });
                        setJustPaid({ ref: result.ref, n: result.installment, paymentId: resp.razorpay_payment_id });
                        await load();
                    } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail) || "Verification failed — if money was deducted, share the payment ID with our team.");
                    }
                },
                modal: { ondismiss: () => toast("Payment window closed — nothing was charged.") },
            });
            rzp.on("payment.failed", (resp) => {
                toast.error(`Payment failed: ${resp.error?.description || "please try another method"}.`);
                load();
            });
            rzp.open();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment right now.");
        } finally { setBusy(false); }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-4">
                <div className="w-full max-w-md rounded-2xl border border-stone-300 bg-white p-8 text-center shadow-xl" data-testid="tourpay-error">
                    <img src="/logo.png" alt="GhumneChaley.com" className="h-10 w-auto mx-auto" />
                    <p className="mt-6 font-display font-black text-lg text-navy">Link unavailable</p>
                    <p className="mt-2 text-sm text-stone-500">{error}</p>
                </div>
            </div>
        );
    }
    if (!data) {
        return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>;
    }

    const t = data.tour;
    const cur = data.current;
    const payable = cur.status !== "paid" && data.status !== "cancelled";
    const wa = (data.contact?.whatsapp || "").replace(/\D/g, "");

    return (
        <div className="min-h-screen bg-cream" data-testid="tourpay-page">
            <header className="bg-navy-deep">
                <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
                    <img src="/logo.png" alt="GhumneChaley.com" className="h-9 w-auto" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white/60 flex items-center gap-1.5"><Lock className="h-3 w-3" /> Secure proposal link</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
                {justPaid && (
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5 flex items-start gap-3" data-testid="tourpay-success">
                        <CheckCircle2 className="h-6 w-6 text-green-700 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-display font-black text-green-800">Payment confirmed — thank you!</p>
                            <p className="text-sm text-green-800/80 mt-1">Instalment {justPaid.n} for {justPaid.ref} is verified. Payment ID: {justPaid.paymentId}. A confirmation email is on its way.</p>
                        </div>
                    </div>
                )}

                <section className="rounded-2xl border border-stone-300 bg-white p-6 shadow-xl">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Proposal {data.ref}</p>
                            <h1 className="font-display font-black text-2xl text-navy mt-1" data-testid="tourpay-tour-name">{t.name}</h1>
                            <p className="text-sm text-stone-500 mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-saffron" /> {t.destination}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Total tour cost</p>
                            <p className="font-display font-black text-xl text-navy" data-testid="tourpay-total">{inr(data.total_cost)}</p>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-stone-600">
                        {(t.start_date || t.end_date) && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-saffron" /> {t.start_date || "?"} → {t.end_date || "?"}</span>}
                        {(t.days || t.nights) && <span className="font-bold">{t.days || "—"}D / {t.nights || "—"}N</span>}
                        <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-saffron" /> {t.travellers} travellers</span>
                        <span>Booked for: <b>{data.customer.name}</b> ({data.customer.phone})</span>
                    </div>
                    {t.itinerary && (
                        <div className="mt-5">
                            <h2 className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Itinerary & details</h2>
                            <p className="mt-2 text-sm text-stone-600 whitespace-pre-line" data-testid="tourpay-itinerary">{t.itinerary}</p>
                        </div>
                    )}
                    {(t.inclusions.length > 0 || t.exclusions.length > 0) && (
                        <div className="mt-5 grid sm:grid-cols-2 gap-4">
                            {t.inclusions.length > 0 && (
                                <div><h2 className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Inclusions</h2>
                                    <ul className="mt-2 space-y-1">{t.inclusions.map((x, i) => <li key={i} className="text-sm text-stone-600 flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />{x}</li>)}</ul></div>
                            )}
                            {t.exclusions.length > 0 && (
                                <div><h2 className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Exclusions</h2>
                                    <ul className="mt-2 space-y-1">{t.exclusions.map((x, i) => <li key={i} className="text-sm text-stone-600 flex gap-2">–{x}</li>)}</ul></div>
                            )}
                        </div>
                    )}
                    {t.notes && <p className="mt-4 text-xs text-stone-500 italic">{t.notes}</p>}
                </section>

                <section className="rounded-2xl border border-stone-300 bg-white p-6 shadow-xl" data-testid="tourpay-schedule">
                    <h2 className="font-display font-black text-lg text-navy">Payment Schedule</h2>
                    <div className="mt-3 space-y-2">
                        {data.installments.map((x) => (
                            <div key={x.n} className={`flex items-center gap-3 rounded-xl border p-3 ${x.n === cur.n ? "border-saffron bg-saffron/5" : "border-stone-200"}`} data-testid={`tourpay-inst-${x.n}`}>
                                <span className={`h-7 w-7 rounded-full text-xs font-black flex items-center justify-center ${x.status === "paid" ? "bg-green-600 text-white" : "bg-navy text-white"}`}>
                                    {x.status === "paid" ? <CheckCircle2 className="h-4 w-4" /> : x.n}
                                </span>
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-navy">{inr(x.amount)}</p>
                                    <p className="text-[11px] text-stone-500">Due {x.due_date}{x.expired && x.status !== "paid" ? " · overdue" : ""}</p>
                                </div>
                                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${INST_CHIP[x.status] || INST_CHIP.pending}`} data-testid={`tourpay-inst-${x.n}-status`}>{x.expired && x.status !== "paid" ? "expired" : x.status}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex justify-between text-xs text-stone-500">
                        <span>Collected: <b className="text-green-700">{inr(data.collected)}</b></span>
                        <span>Balance remaining: <b className="text-navy">{inr(data.outstanding)}</b></span>
                    </div>
                </section>

                {payable && (
                    <section className="rounded-2xl border-2 border-saffron bg-white p-6 shadow-xl text-center" data-testid="tourpay-paynow">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">This link is for instalment {cur.n}</p>
                        <p className="font-display font-black text-3xl text-navy mt-1" data-testid="tourpay-due-amount">{inr(cur.amount)}</p>
                        <p className="text-xs text-stone-500 mt-1">Due by {cur.due_date}</p>
                        <button onClick={payNow} disabled={busy} data-testid="tourpay-pay-btn"
                            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-saffron px-10 py-3.5 font-display font-black text-sm uppercase tracking-wider text-white hover:bg-saffron-dark disabled:opacity-60">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Pay {inr(cur.amount)} Now
                        </button>
                        <p className="mt-3 text-[11px] text-stone-400">Secure payment via Razorpay · UPI, cards, net banking. Your payment is verified on our servers before it is marked paid.</p>
                    </section>
                )}
                {cur.status === "paid" && !justPaid && (
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-center" data-testid="tourpay-already-paid">
                        <p className="font-display font-black text-green-800">Instalment {cur.n} is already paid</p>
                        <p className="text-sm text-green-800/80 mt-1">Paid {cur.paid_at ? cur.paid_at.replace("T", " ").slice(0, 19) + " UTC" : ""} · Ref {cur.payment_id}</p>
                    </div>
                )}

                <section className="rounded-2xl border border-stone-300 bg-white p-6 shadow-xl" data-testid="tourpay-contact">
                    <h2 className="font-display font-black text-navy">Need a correction?</h2>
                    <p className="text-sm text-stone-500 mt-1">Your trip is handled by <b className="text-navy">{data.sales_name || "our sales team"}</b>. If any tour detail looks wrong, reach us before paying:</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {wa && <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi! About proposal ${data.ref} (${t.name}) — `)}`} target="_blank" rel="noreferrer" data-testid="tourpay-wa" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-green-700 text-white text-xs font-black uppercase tracking-wider hover:bg-green-800"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp us</a>}
                        {data.contact?.email && <a href={`mailto:${data.contact.email}?subject=${encodeURIComponent(`Proposal ${data.ref} — correction request`)}`} data-testid="tourpay-email" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-stone-300 text-xs font-black uppercase tracking-wider text-navy hover:border-navy"><Mail className="h-3.5 w-3.5" /> Email us</a>}
                    </div>
                </section>

                <p className="text-center text-[11px] text-stone-400 pb-6">
                    Quote proposal <b>{data.ref}</b> in any message. · <Link to="/" className="underline">GhumneChaley.com</Link>
                </p>
            </main>
        </div>
    );
}
